import { copyIcon, flagIcon, replyIcon } from './icons';
import { t, tf } from './i18n';

const ROOT_CLASS = 'flix-chat-widget__msg-menu';

// The reactions offered — must match the server's ALLOWED_REACTION_EMOJIS
// (features/messages/entity.ts). Anything outside this set is rejected by
// the API, so the widget only ever offers these five.
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '🙏', '🎮'] as const;

export interface MessageMenuHandlers {
  onReact: (emoji: string) => void;
  onReply: () => void;
  onCopy: () => void;
  onReport: () => void;
}

export interface MessageMenuOpenOptions {
  x: number;
  y: number;
  activeEmojis: Set<string>;
  // Copy is only offered when the message actually has text to copy.
  canCopy: boolean;
  handlers: MessageMenuHandlers;
}

export interface MessageMenu {
  element: HTMLElement;
  open(options: MessageMenuOpenOptions): void;
  close(): void;
  isOpen(): boolean;
  destroy(): void;
}

// A single floating menu (reactions row + Reply / Copy / Report) reused for
// every message — opened at a point, positioned within the viewport, and
// closed on the next outside click, Escape, scroll, or resize. Modelled on
// the overflow menu's open/close discipline.
export function createMessageMenu(): MessageMenu {
  const element = document.createElement('div');
  element.className = ROOT_CLASS;
  element.setAttribute('role', 'menu');
  element.setAttribute('aria-label', t('messageActions'));
  element.hidden = true;

  const reactionsRow = document.createElement('div');
  reactionsRow.className = `${ROOT_CLASS}-reactions`;

  const actions = document.createElement('div');
  actions.className = `${ROOT_CLASS}-actions`;

  element.append(reactionsRow, actions);

  let open = false;

  function close(): void {
    if (!open) {
      return;
    }
    open = false;
    element.hidden = true;
    reactionsRow.replaceChildren();
    actions.replaceChildren();
    document.removeEventListener('pointerdown', handleOutside, true);
    document.removeEventListener('keydown', handleKeydown, true);
    window.removeEventListener('resize', close);
    window.removeEventListener('scroll', close, true);
  }

  function handleOutside(event: Event): void {
    if (!element.contains(event.target as Node)) {
      close();
    }
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  }

  function actionItem(icon: SVGSVGElement, label: string, danger: boolean, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `${ROOT_CLASS}-item${danger ? ` ${ROOT_CLASS}-item--danger` : ''}`;
    button.setAttribute('role', 'menuitem');
    button.append(icon, document.createTextNode(label));
    button.addEventListener('click', () => {
      onClick();
      close();
    });
    return button;
  }

  return {
    element,

    open(options: MessageMenuOpenOptions): void {
      reactionsRow.replaceChildren();
      actions.replaceChildren();

      for (const emoji of REACTION_EMOJIS) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `${ROOT_CLASS}-reaction${
          options.activeEmojis.has(emoji) ? ` ${ROOT_CLASS}-reaction--active` : ''
        }`;
        button.textContent = emoji;
        button.setAttribute('aria-label', tf('addReaction', { emoji }));
        button.addEventListener('click', () => {
          options.handlers.onReact(emoji);
          close();
        });
        reactionsRow.append(button);
      }

      actions.append(actionItem(replyIcon(), t('reply'), false, options.handlers.onReply));
      if (options.canCopy) {
        actions.append(actionItem(copyIcon(), t('copyText'), false, options.handlers.onCopy));
      }
      actions.append(actionItem(flagIcon(), t('report'), true, options.handlers.onReport));

      // Show first (off-screen) to measure, then clamp within the viewport.
      element.hidden = false;
      open = true;
      const rect = element.getBoundingClientRect();
      const margin = 8;
      let left = options.x;
      let top = options.y;
      if (left + rect.width + margin > window.innerWidth) {
        left = window.innerWidth - rect.width - margin;
      }
      if (top + rect.height + margin > window.innerHeight) {
        top = options.y - rect.height;
      }
      element.style.left = `${Math.max(margin, left)}px`;
      element.style.top = `${Math.max(margin, top)}px`;

      // Deferred so the opening pointerdown/contextmenu doesn't immediately
      // close it.
      setTimeout(() => {
        if (open) {
          document.addEventListener('pointerdown', handleOutside, true);
          document.addEventListener('keydown', handleKeydown, true);
          window.addEventListener('resize', close);
          window.addEventListener('scroll', close, true);
        }
      }, 0);
    },

    close,

    isOpen(): boolean {
      return open;
    },

    destroy(): void {
      close();
      element.remove();
    },
  };
}
